import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../reusable/DataTable/DataTable';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import Button from '../../../reusable/Button/Button';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import LineChart from '../../../reusable/LineChart/LineChart';
import {
  formatCurrencyAbbreviated,
  formatCurrency,
  formatAxisDate,
} from '../../../reusable/LineChart/formatters';
import DateSelector from '../../../reusable/DateSelector/DateSelector';
import { debounce } from '../../../utilities/debounce';
import {
  useGetAffiliateDashboardCardsQuery,
  useGetAffiliateDashboardTrendQuery,
  useGetAffiliateRecentRequestsQuery,
} from '../../../store/api/affiliateApi';
import {
  METRIC_PARAM_MAP,
  TREND_RANGES,
  buildMetricsCards,
  trendSummary,
  toChartPoints,
} from './data';
import { t } from '../../../i18n/i18n';
import './PartnerProgramme.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMATTER.format(d);
};

const formatIntegerAbbreviated = (value) => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}${Math.round(abs / 100_000) / 10}M`;
  if (abs >= 1_000) return `${sign}${Math.round(abs / 100) / 10}K`;
  return `${sign}${Math.round(abs)}`;
};

/** `status` as the API takes it; "All" is the param left off. */
const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Processing', value: 'Processing' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

const PAGE_SIZE = 10;
const SEARCH_DELAY = 300;

/** Where each request type's "View" lands — its own Affiliate tab. */
const VIEW_ROUTES = {
  giveaway: '/affiliate/giveaway',
  personal_account: '/affiliate/personal-account',
  retainer_payout: '/affiliate/retainer',
};

/** The card range the dashboard opens on — the API's own default. */
const DEFAULT_RANGE = '1m';

/**
 * Affiliate — Partner Programme (dashboard). Default landing page for the
 * Affiliate section — 1:16777. Cards, trend and recent requests come from
 * `/affiliate/dashboard/{cards,trend,recent-requests}`.
 */
export default function PartnerProgramme() {
  const navigate = useNavigate();

  const [trendMetric, setTrendMetric] = useState('Revenue');
  const [trendRange, setTrendRange] = useState('1M');

  // Unset until the picker is used: the cards then read the last month.
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const hasCustomRange = Boolean(dateRange.startDate && dateRange.endDate);

  const [statusFilter, setStatusFilter] = useState(null);
  const [requestsPage, setRequestsPage] = useState(1);
  const [search, setSearch] = useState('');
  const onSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value.trim());
        setRequestsPage(1);
      }, SEARCH_DELAY),
    []
  );

  /* ---- 1. cards ---- */
  const cardParams = hasCustomRange
    ? { start_date: dateRange.startDate, end_date: dateRange.endDate }
    : { range: DEFAULT_RANGE };
  const {
    data: cardsResponse,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useGetAffiliateDashboardCardsQuery(cardParams);
  const isMetricsLoading = isStatsLoading || isStatsFetching;
  const metrics = useMemo(() => buildMetricsCards(cardsResponse?.cards), [cardsResponse]);

  /* ---- 2. trend: all four series in one call, so the metric tabs don't refetch ---- */
  const rangeParam = trendRange.toLowerCase();
  const {
    data: trendResponse,
    isLoading: isChartLoading,
    isFetching: isChartFetching,
  } = useGetAffiliateDashboardTrendQuery({ range: rangeParam, metric: 'all' });

  // The same range's cards, for the "+$1,940.20 +18.4%" beside the total.
  // When the range is the cards' own default this is the request above, cached.
  const { data: trendCards } = useGetAffiliateDashboardCardsQuery({ range: rangeParam });

  const metricKey = METRIC_PARAM_MAP[trendMetric] || 'revenue';
  const isRevenueMetric = metricKey === 'revenue';
  const series = trendResponse?.series?.[metricKey];
  const chartPoints = useMemo(() => toChartPoints(series), [series]);
  const summary = trendSummary(metricKey, series, trendCards?.cards?.[metricKey]);

  /* ---- 3. recent requests ---- */
  const requestsParams = {
    page: requestsPage,
    page_size: PAGE_SIZE,
    ...(statusFilter && { status: statusFilter }),
    ...(search && { search }),
  };
  const {
    data: requestsResponse,
    isLoading: isRequestsLoading,
    isFetching: isRequestsFetching,
    isError: isRequestsError,
  } = useGetAffiliateRecentRequestsQuery(requestsParams);

  const requestRows = requestsResponse?.results ?? [];
  const totalRequestsCount = requestsResponse?.count ?? requestRows.length;

  const columns = useMemo(
    () => [
      {
        key: 'type',
        header: 'Request Type',
        width: 14,
        value: (r) => t(r.request_type_display || r.request_type || 'Request'),
      },
      {
        key: 'title',
        header: 'Details',
        width: 22,
        value: (r) => r.title || '—',
      },
      {
        key: 'date',
        header: 'Date',
        width: 12,
        value: (r) => formatDate(r.date),
      },
      {
        key: 'status',
        header: 'Status',
        width: 12,
        render: (r) => <StatusChip>{t(r.status || 'Processing')}</StatusChip>,
      },
      {
        key: 'view',
        header: 'View Request',
        width: 12,
        searchable: false,
        render: (r) => {
          const to = VIEW_ROUTES[r.request_type];
          return (
            <Button
              variant="pill"
              height={28.8}
              paddingInline={16}
              disabled={!to}
              onClick={() => to && navigate(to)}
            >
              {t('View')}
            </Button>
          );
        },
      },
    ],
    [navigate]
  );

  const isChartActive = isChartLoading || isChartFetching;
  const isRequestsActive = isRequestsLoading || isRequestsFetching;

  return (
    <div className="affiliate-dashboard">
      <div className="affiliate-dashboard__toolbar">
        <DateSelector
          value={dateRange}
          onChange={(next) => setDateRange(next)}
          label={hasCustomRange ? undefined : t('Last 30 days')}
        />
      </div>

      <div className="affiliate-metrics">
        {metrics.map((m) => (
          <div className="affiliate-metric" key={m.key}>
            <div className="affiliate-metric__head">
              <span className="icon affiliate-metric__icon" style={icon(m.icon)} />
              <span className="affiliate-metric__label">{t(m.label)}</span>
            </div>
            <div className="affiliate-metric__value">
              {isMetricsLoading ? <Skeleton w="100px" h={24} /> : m.value}
            </div>
            <p className={`affiliate-metric__note${m.noteTone ? ` is-${m.noteTone}` : ''}`}>
              {isMetricsLoading ? <Skeleton w="120px" h={12} /> : m.note}
            </p>
          </div>
        ))}
      </div>

      <section className="affiliate-trend">
        <header className="affiliate-trend__head">
          <div className="affiliate-trend__left">
            <span className="affiliate-trend__title">
              <span className="icon affiliate-trend__icon" style={icon('affiliate-revenue-trend')} />
              {trendMetric === 'Revenue'
                ? t('Revenue trend')
                : trendMetric === 'Click'
                  ? t('Clicks trend')
                  : trendMetric === 'Sales'
                    ? t('Sales trend')
                    : t('Sign-ups trend')}
            </span>
            <SegmentedTabs
              radius="track"
              options={Object.keys(METRIC_PARAM_MAP)}
              value={trendMetric}
              onChange={setTrendMetric}
            />
          </div>
          <SegmentedTabs
            radius="track"
            options={TREND_RANGES}
            value={trendRange}
            onChange={setTrendRange}
          />
        </header>

        <div className="affiliate-trend__summary">
          {isChartActive ? (
            <Skeleton w="164px" h={34} />
          ) : (
            <>
              <span className="affiliate-trend__value">{summary.value}</span>
              {summary.delta && (
                <span
                  className={`affiliate-trend__delta${summary.deltaTone ? ` is-${summary.deltaTone}` : ''}`}
                >
                  {summary.delta}
                </span>
              )}
            </>
          )}
        </div>

        <LineChart
          className="affiliate-trend__plot"
          data={chartPoints}
          yAxisFormatter={isRevenueMetric ? formatCurrencyAbbreviated : formatIntegerAbbreviated}
          tooltipFormatter={
            isRevenueMetric
              ? (p) => `${formatAxisDate(p.x)} ${formatCurrency(p.y)}`
              : (p) => `${formatAxisDate(p.x)} ${Number(p.y).toLocaleString('en-US')}`
          }
          height={264}
          loading={isChartActive}
          emptyMessage={t('No activity in this period')}
        />
      </section>

      <DataTable
        icon="affiliate-recent-requests"
        title="Recent Requests"
        count={isRequestsLoading ? '—' : `${totalRequestsCount}`}
        columns={columns}
        rows={requestRows}
        getRowId={(r) => r.id}
        pageSize={PAGE_SIZE}
        page={requestsPage}
        onPageChange={setRequestsPage}
        total={totalRequestsCount}
        loading={isRequestsActive}
        searchPlaceholder="Search requests"
        onSearch={onSearch}
        emptyMessage={
          isRequestsError
            ? 'Could not load your requests'
            : statusFilter || search
              ? 'No requests match your search or filter'
              : 'No requests yet'
        }
        filter={{
          options: STATUS_OPTIONS,
          value: statusFilter,
          onChange: (val) => {
            setStatusFilter(val);
            setRequestsPage(1);
          },
        }}
      />
    </div>
  );
}
